import { cloneDeep } from 'lodash';
import { t } from 'ttag';
import { GroupLine, GroupLineDisplay, GroupLineOrder } from './api/group';
import { Item, TMenu } from '../contexts/site/site-context';

const STORAGE_KEY = 'group-order';

// What this browser needs to remember to carry on in a group order after a reload
export type StoredGroup = {
    token: string;
    participantToken: string;
    siteUid: string;
    isLead: boolean;
};

export const rememberGroup = (group: StoredGroup): void => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(group));
    } catch (e) {
        // no storage, no resuming
    }
};

export const storedGroup = (): StoredGroup | null => {
    try {
        const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? 'null');

        return stored && typeof stored.token === 'string' && typeof stored.participantToken === 'string'
            ? stored
            : null;
    } catch (e) {
        return null;
    }
};

export const forgetGroup = (): void => {
    try {
        window.localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
        // nothing to forget
    }
};

// The link the lead shares: straight onto the site's menu, with the group order to join
export const shareLink = (origin: string, channelHandle: string, siteId: string, token: string): string =>
    `${origin}/${channelHandle}/${encodeURIComponent(siteId)}?group=${encodeURIComponent(token)}`;

// What goes to the API for a basket line: what gets validated, and what the basket shows
export const toGroupLine = (item: Item): { lineId: string; order: GroupLineOrder; display: GroupLineDisplay } => ({
    lineId: item.basketIndex ?? '',
    order: {
        item_id: item.uid,
        quantity: item.quantity ?? 1,
        modifiers: (item.modifiers ?? []).map((mod) => ({ modifier_id: mod.uid, quantity: mod.quantity })),
    },
    // (the full item, with all its modifier groups, is on the menu already, so it isn't sent back and forth)
    display: {
        name: item.name,
        price: item.price,
        modifiersTotal: item.modifiersTotal ?? 0,
        modifiers: (item.modifiers ?? []).map((mod) => ({
            uid: mod.uid,
            name: mod.name,
            quantity: mod.quantity,
            group_uid: mod.group_uid,
        })),
    },
});

// The basket line as the rest of the ordering site knows it, with its modifiers selected, so it can be
// shown, edited and removed like any other. When the item has left the menu there's still enough to show it.
export const fromGroupLine = (line: GroupLine, menu: TMenu | null): Item => {
    const onMenu = menu?.categories
        .flatMap((category) => category.items)
        .find((item) => item.uid === line.order.item_id);

    const item: Item = onMenu
        ? cloneDeep(onMenu)
        : ({
            uid: line.order.item_id,
            name: line.display.name,
            description: '',
            price: line.display.price,
            modifier_groups: [],
        } as unknown as Item);

    const selected = new Map(line.order.modifiers.map((mod) => [mod.modifier_id, mod.quantity]));

    item.modifier_groups.forEach((group) =>
        group.modifiers.forEach((mod) => {
            const quantity = selected.get(mod.uid) ?? 0;

            // (an unselected default is told apart by -1 in the basket, and is 0 in the modal)
            mod.quantity = quantity > 0 ? quantity : 0;
        })
    );

    item.modifiers = line.display.modifiers;
    item.modifiersTotal = line.display.modifiersTotal;
    item.quantity = line.order.quantity;
    item.basketIndex = line.line_id;
    item.groupLine = { participant: line.participant, mine: line.mine, problem: line.problem };

    return item;
};

export type LineChanges = {
    // lines that are new or have changed
    upserts: Item[];
    // lines that have gone
    removals: string[];
};

// What has to be told to the API for the basket to go from one set of lines to the next
export const lineChanges = (before: Item[], after: Item[]): LineChanges => {
    const was = new Map(before.filter((i) => i.basketIndex).map((i) => [i.basketIndex as string, JSON.stringify(toGroupLine(i))]));
    const is = new Set(after.filter((i) => i.basketIndex).map((i) => i.basketIndex as string));

    return {
        upserts: after.filter((i) => i.basketIndex && was.get(i.basketIndex) !== JSON.stringify(toGroupLine(i))),
        removals: Array.from(was.keys()).filter((lineId) => !is.has(lineId)),
    };
};

// What guests are told, wherever they meet it, while the lead is choosing a time and checking out
export const groupLockedMessage = (host: string | null): string => {
    const name = host ?? t`The host`;

    return t`${name} is choosing a time and checking out. You can't add items right now.`;
};
